// interruptionCopy — the words for the ONE interruption notice (D18, FR-2.5),
// per kind: what happened, what is kept, and the one thing you can do. Pure:
// returns Lingui descriptors; the notice localizes them. Exhaustive over
// `Interruption`, so a new kind cannot ship without its copy. A refused
// request reuses `toDisplay` (the same text the toast shows), so the notice
// and the toast never disagree.

import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { toDisplay } from "../../../app/externalError.js";
import type { Interruption, InterruptionAction } from "../types.js";

// Colour by consequence (always with an icon too): `neutral` = nothing needs
// doing, `attention` = needs you, `blocked` = you cannot continue here.
export type InterruptionTone = "neutral" | "attention" | "blocked";

type ActionCopy = {
    readonly type: InterruptionAction;
    readonly label: MessageDescriptor;
};

export type InterruptionCopy = {
    readonly tone: InterruptionTone;
    readonly happened: MessageDescriptor;
    // What is kept, then (optionally) more about the cause.
    readonly kept: readonly MessageDescriptor[];
    // Absent when the app has no working recovery for this kind.
    readonly action?: ActionCopy;
};

const continueAction: ActionCopy = {
    type: "continue",
    label: msg`Continue where it left off`,
};
const tryAgain: ActionCopy = { type: "try_again", label: msg`Try again` };

const savedSoFar = msg`Everything finished before this is saved.`;
const notSent = msg`Your message was not sent.`;

export const interruptionCopy = (cause: Interruption): InterruptionCopy => {
    switch (cause.type) {
        case "interrupted":
            return {
                tone: "neutral",
                happened: msg`You stopped the reply`,
                kept: [
                    msg`Everything the assistant finished before that is saved.`,
                ],
                action: continueAction,
            };
        case "typing_while_working":
            return {
                tone: "neutral",
                happened: msg`The assistant is still working`,
                kept: [
                    msg`Your new message stays here. Send it when the assistant is done, or stop it now.`,
                ],
                action: { type: "stop", label: msg`Stop the assistant` },
            };
        case "connection_lost":
            return cause.messageSaved
                ? {
                      tone: "attention",
                      happened: msg`The connection was lost`,
                      kept: [savedSoFar],
                      action: continueAction,
                  }
                : {
                      tone: "attention",
                      happened: msg`The connection was lost`,
                      kept: [notSent],
                      action: tryAgain,
                  };
        case "failed": {
            if (cause.error === undefined)
                return {
                    tone: "attention",
                    happened: msg`Something went wrong`,
                    kept: [savedSoFar],
                    action: tryAgain,
                };
            const d = toDisplay(cause.error);
            const kept = d.body === undefined ? [notSent] : [notSent, d.body];
            return d.retriable
                ? {
                      tone: "attention",
                      happened: d.title,
                      kept,
                      action: tryAgain,
                  }
                : { tone: "blocked", happened: d.title, kept };
        }
        case "budget_halted":
            return {
                tone: "neutral",
                happened: msg`Paused to keep costs down`,
                kept: [savedSoFar],
                action: continueAction,
            };
        case "budget_used_up":
            return {
                tone: "blocked",
                happened: msg`The AI budget is used up`,
                kept: [
                    savedSoFar,
                    msg`Ask your administrator to add more. You can keep editing as usual.`,
                ],
            };
        case "context_window_exceeded":
            return {
                tone: "attention",
                happened: msg`The conversation has become too long`,
                kept: [
                    msg`The assistant can't keep all of it in mind. Everything is saved.`,
                ],
                action: {
                    type: "new_conversation",
                    label: msg`Continue in a new conversation`,
                },
            };
        case "unfinished":
            return {
                tone: "attention",
                happened: msg`The work stopped before it was done`,
                kept: [savedSoFar],
                action: continueAction,
            };
        case "colleague_clash":
            return {
                tone: "attention",
                happened: msg`A colleague changed this at the same time`,
                kept: [msg`Nothing was overwritten.`],
            };
        case "signed_out":
            return {
                tone: "blocked",
                happened: msg`You have been signed out`,
                kept: [
                    msg`Everything before your last message is saved. Your last message was not sent.`,
                ],
                action: { type: "sign_in", label: msg`Sign in again` },
            };
        case "unknown":
            return {
                tone: "attention",
                happened: msg`The assistant stopped unexpectedly`,
                kept: [savedSoFar],
                action: tryAgain,
            };
        default:
            return exhaustSwitchCase(cause);
    }
};
